import React from "react";
import { Modal } from "react-bootstrap";
import FlowReport, { FlowReportGrid, MultiFlowConfig } from "../../../pages/FlowReport/FlowReport";
import { FlowReport as FlowReportType } from "./FlowReportModal.utils";
import styles from "./FlowReportModal.module.css";

interface FlowReportModalProps {
  /**
   * Controls whether the modal is visible
   */
  show: boolean;

  /**
   * Callback fired when the modal should be closed
   */
  onHide: () => void;

  /**
   * Report mode: 'single' for single-flow, 'multi' for multi-flow
   */
  reportMode: "single" | "multi";

  /**
   * For single mode: The unique identifier for the flow/workflow
   */
  flowId?: number;

  /**
   * For multi mode: The report type identifier (e.g., '8879')
   */
  reportType?: string;

  /**
   * For multi mode: Full configuration object from backend
   */
  multiFlowConfig?: MultiFlowConfig;

  /**
   * Optional flow name to display in the report title
   */
  flowName?: string;

  /**
   * API configuration for making requests
   */
  apiConfig: {
    baseUri: string;
    token: string;
  };

  /**
   * Optional callback fired when report data is successfully loaded
   */
  onReportLoaded?: (report: FlowReportType, grid: FlowReportGrid | null) => void;

  /**
   * Optional callback fired when an error occurs
   */
  onError?: (error: Error) => void;

  /**
   * Initial page size for the grid (default: 50)
   */
  initialPageSize?: number;

  /**
   * Beautified action names for display
   */
  beautifiedNames?: Record<string, string>;

  /**
   * Base column header overrides
   */
  baseColumnHeaders?: Record<string, string>;
}

/**
 * FlowReportModal Component
 *
 * A modal wrapper for the FlowReport component. Provides a full-screen modal
 * experience for viewing flow reports.
 *
 * Supports two modes:
 * - Single: Traditional single-flow report using FlowId
 * - Multi: Multi-flow report using reportType and CygnusExecutionId joins
 *
 * @example Single Mode
 * ```tsx
 * <FlowReportModal
 *   show={showModal}
 *   onHide={() => setShowModal(false)}
 *   reportMode="single"
 *   flowId={123}
 *   flowName="My Workflow"
 *   apiConfig={{
 *     baseUri: process.env.REACT_APP_BASE_URI,
 *     token: localStorage.getItem("token")
 *   }}
 * />
 * ```
 *
 * @example Multi Mode
 * ```tsx
 * <FlowReportModal
 *   show={showModal}
 *   onHide={() => setShowModal(false)}
 *   reportMode="multi"
 *   reportType="8879"
 *   flowName="E-file Authorization Report"
 *   multiFlowConfig={config}
 *   apiConfig={{
 *     baseUri: process.env.REACT_APP_BASE_URI,
 *     token: localStorage.getItem("token")
 *   }}
 * />
 * ```
 */
const FlowReportModal: React.FC<FlowReportModalProps> = ({
  show,
  onHide,
  reportMode,
  flowId,
  reportType,
  multiFlowConfig,
  flowName,
  onReportLoaded,
  onError,
  initialPageSize = 50,
  beautifiedNames,
  baseColumnHeaders,
}) => {
  // Determine endpoints based on mode
  const reportEndpoint =
    reportMode === "multi"
      ? "/workflow/flow/multi-report/"
      : "/workflow/flow/report/";

  const gridEndpoint =
    reportMode === "multi"
      ? "/workflow/flow/multi-grid/"
      : "/workflow/flow/grid/";

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      size="xl"
      dialogClassName={styles.modal}
      contentClassName={styles.modalContent}
    >
      <Modal.Body className={styles.modalBodyNoPadding}>
        {show && (
          <FlowReport
            reportMode={reportMode}
            flowId={flowId}
            reportType={reportType}
            multiFlowConfig={multiFlowConfig}
            flowName={flowName}
            reportEndpoint={reportEndpoint}
            gridEndpoint={gridEndpoint}
            onReportLoaded={onReportLoaded}
            onError={onError}
            initialPageSize={initialPageSize}
            showHeader={true}
            showActionStats={true}
            beautifiedNames={beautifiedNames}
            baseColumnHeaders={baseColumnHeaders}
          />
        )}
      </Modal.Body>
    </Modal>
  );
};

export default FlowReportModal;